import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { getApiClient } from '../../../shared/types/api-client'
import { CheckCategory, ReleaseCheckItem } from '../types/ReleaseCheckItem'
import { RunStatus, SUB_ORCHESTRATED_CATEGORIES } from '../types/RunStatus'

const SCOPE = 'releaseCheck'
const ITEMS = 'items'
const STATUS = 'status'
const LIST = 'list'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  items: () => [{ scope: SCOPE, entity: ITEMS, kind: LIST }] as const,
  status: () => [{ scope: SCOPE, entity: STATUS }] as const,
}

const getItems = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.get('release-check/items')
  return response.data as ReleaseCheckItem[]
}

export function useReleaseCheckItems(): [ReleaseCheckItem[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.items()],
    queryFn: getItems,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

const getStatus = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.get('release-check/status')
  return response.data as RunStatus
}

const TERMINAL_STATES: readonly RunStatus['state'][] = ['Idle', 'Completed', 'Failed', 'Terminated', 'Canceled']

const isInFlight = (s: RunStatus['state'] | undefined): boolean =>
  !!s && !TERMINAL_STATES.includes(s)

export function useReleaseCheckStatus(): [RunStatus | undefined, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.status()],
    queryFn: getStatus,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      const anyRunning =
        isInFlight(data.state) ||
        Object.values(data.categories ?? {}).some((c) => isInFlight(c?.state))
      return anyRunning ? 5000 : false
    },
  })
  return [data, isFetching]
}

// Map of orchestration key -> currently in-flight. Includes the umbrella run
// plus every sub-orchestrated category.
const runningMap = (status: RunStatus | undefined): Record<string, boolean> => {
  const map: Record<string, boolean> = { umbrella: isInFlight(status?.state) }
  for (const c of SUB_ORCHESTRATED_CATEGORIES) {
    map[c] = isInFlight(status?.categories?.[c]?.state)
  }
  return map
}

// Refreshes the items query (discrepancy list + nav counts) whenever any
// orchestration transitions from running to finished. Mount once on the page
// that shows the results; the status query already polls while runs are active.
export function useRefreshItemsOnRunComplete(): void {
  const [status] = useReleaseCheckStatus()
  const queryClient = useQueryClient()
  const previous = useRef(runningMap(undefined))

  useEffect(() => {
    const current = runningMap(status)
    const justFinished = Object.keys(current).some((k) => previous.current[k] && !current[k])
    previous.current = current
    if (justFinished) {
      void queryClient.invalidateQueries({ queryKey: keysFactory.items() })
    }
  }, [status, queryClient])
}

interface StartRunOptions {
  force?: boolean
}

export function useStartReleaseCheckRun() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (options?: StartRunOptions) => {
      const apiClient = getApiClient()
      const url = options?.force ? 'release-check/runs?force=true' : 'release-check/runs'
      const response = await apiClient.post(url)
      return response.data as { state: string }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.all() })
    },
  })
  return [mutateAsync, isPending] as const
}

interface StartCategoryRunVars {
  category: CheckCategory
  force?: boolean
}

export function useStartReleaseCheckCategoryRun() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, variables } = useMutation({
    mutationFn: async ({ category, force }: StartCategoryRunVars) => {
      const apiClient = getApiClient()
      const url = force
        ? `release-check/runs/${category}?force=true`
        : `release-check/runs/${category}`
      const response = await apiClient.post(url)
      return response.data as { state: string }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.all() })
    },
  })
  return [mutateAsync, isPending, variables?.category] as const
}

interface SetIgnoredParams {
  category: CheckCategory
  id: string
  ignored: boolean
}

export function useSetItemIgnored() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ category, id, ignored }: SetIgnoredParams) => {
      const apiClient = getApiClient()
      const response = await apiClient.patch(
        `release-check/items/${category}/${encodeURIComponent(id)}`,
        { ignored },
      )
      return response.data as ReleaseCheckItem
    },
    onSuccess: async (updated) => {
      // Instant feedback: swap the toggled item in place.
      queryClient.setQueryData<ReleaseCheckItem[]>([...keysFactory.items()], (items) =>
        items?.map((s) => (s.id === updated.id && s.category === updated.category ? updated : s)),
      )
      // Reconcile the list + nav counts with server truth.
      await queryClient.invalidateQueries({ queryKey: keysFactory.items() })
    },
  })
  return [mutateAsync, isPending] as const
}
